import { Routes } from '@angular/router';
import { authGuard, practiceGuard, loginRedirectGuard, onboardingGuard } from './guards/auth.guard';
import { permissionGuard, anyPermissionGuard } from './guards/permission.guard';
import { featureFlagGuard } from './guards/feature-flag.guard';
import { ShellComponent } from './layout/shell.component';

export const routes: Routes = [
  // ─── Auth routes (no shell) ───
  {
    path: 'login',
    canActivate: [loginRedirectGuard],
    loadComponent: () =>
      import('./features/auth/login/login.component').then(m => m.LoginComponent),
  },
  {
    path: 'signup',
    canActivate: [loginRedirectGuard],
    loadComponent: () =>
      import('./features/auth/signup/signup.component').then(m => m.SignupComponent),
  },
  {
    path: 'auth/callback',
    loadComponent: () =>
      import('./features/auth/auth-callback/auth-callback.component').then(m => m.AuthCallbackComponent),
  },
  {
    path: 'onboarding',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/auth/onboarding/onboarding.component').then(m => m.OnboardingComponent),
  },
  {
    path: 'practice-selection',
    canActivate: [authGuard, onboardingGuard],
    loadComponent: () =>
      import('./features/auth/practice-selection/practice-selection.component').then(m => m.PracticeSelectionComponent),
  },

  // ─── Shell routes — everything inside the main app uses the shell so the
  //     top nav + bottom nav are always visible (settings, rounds, summaries
  //     etc. are wrapped too).
  {
    path: '',
    component: ShellComponent,
    canActivate: [authGuard, onboardingGuard, practiceGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/home/home.component').then(m => m.HomeComponent),
      },

      {
        path: 'calendar',
        canActivate: [permissionGuard('calendar.view')],
        loadComponent: () =>
          import('./features/calendar/calendar-page.component').then(m => m.CalendarPageComponent),
      },

      {
        path: 'schedule',
        canActivate: [permissionGuard('scheduling.view')],
        loadComponent: () =>
          import('./features/schedule/schedule-summary.component').then(m => m.ScheduleSummaryComponent),
      },

      // Account/practice surfaces
      {
        path: 'settings',
        loadComponent: () =>
          import('./features/settings/settings.component').then(m => m.SettingsComponent),
      },
      {
        path: 'practice-settings',
        canActivate: [
          anyPermissionGuard([
            'practice.view_settings',
            'branches.view',
            'billing.manage',
            'scheduling.manage',
            'scheduling.configure',
            'leave.manage',
          ]),
        ],
        loadChildren: () =>
          import('./features/practice-settings/practice-settings.routes').then(
            m => m.PRACTICE_SETTINGS_ROUTES,
          ),
      },
      {
        path: 'team',
        canActivate: [
          anyPermissionGuard(['members.view', 'members.approve_requests', 'members.invite']),
        ],
        loadChildren: () =>
          import('./features/team/team.routes').then(m => m.TEAM_ROUTES),
      },
      {
        path: 'library',
        canActivate: [
          anyPermissionGuard([
            'medications.view',
            'diagnoses.view',
            'discharge_questions.view',
            'practice.view_settings',
          ]),
        ],
        loadChildren: () =>
          import('./features/practice-library/practice-library.routes').then(
            m => m.PRACTICE_LIBRARY_ROUTES,
          ),
      },
      {
        path: 'audit-log',
        canActivate: [featureFlagGuard('activity_log'), permissionGuard('audit.view')],
        loadComponent: () =>
          import('./features/audit-log/audit-log.component').then(m => m.AuditLogComponent),
      },

      // Rounds + summaries (previously full-screen routes; now wrapped in
      // the shell so the main nav stays in view).
      {
        path: 'hospitalisations/rounds/:hospitalisationId/:roundType',
        canActivate: [permissionGuard('rounds.create')],
        loadComponent: () =>
          import('./features/hospitalisations/rounds-form.component').then(m => m.RoundsFormComponent),
      },
      {
        path: 'hospitalisations/rounds/:hospitalisationId',
        canActivate: [permissionGuard('rounds.create')],
        loadComponent: () =>
          import('./features/hospitalisations/rounds-form.component').then(m => m.RoundsFormComponent),
      },
      {
        path: 'rounds-summary/:roundsId',
        canActivate: [permissionGuard('hospitalisations.view')],
        loadComponent: () =>
          import('./features/hospitalisations/rounds-summary.component').then(m => m.RoundsSummaryComponent),
      },
      {
        path: 'hospitalisation-summary/:hospitalisationId',
        canActivate: [permissionGuard('hospitalisations.view')],
        loadComponent: () =>
          import('./features/hospitalisations/hospitalisation-summary.component').then(m => m.HospitalisationSummaryComponent),
      },

      // Directory — unified Clients + Patients search/list entry point.
      {
        path: 'directory',
        canActivate: [anyPermissionGuard(['clients.view', 'patients.view'])],
        loadComponent: () =>
          import('./features/directory/directory.component').then(m => m.DirectoryComponent),
      },

      // Patients — the bare list now lives in the Directory; detail/add/edit stay.
      {
        path: 'patients',
        redirectTo: 'directory',
        pathMatch: 'full',
      },
      {
        path: 'patients/add',
        canActivate: [permissionGuard('patients.edit')],
        loadComponent: () =>
          import('./features/patients/patient-add.component').then(m => m.PatientAddComponent),
      },
      {
        path: 'patients/patient/:patientId',
        canActivate: [permissionGuard('patients.view')],
        loadComponent: () =>
          import('./features/patients/patient-detail.component').then(m => m.PatientDetailComponent),
      },
      // Back-compat: old links to /detail still resolve to the canonical URL.
      {
        path: 'patients/patient/:patientId/detail',
        redirectTo: 'patients/patient/:patientId',
        pathMatch: 'full',
      },
      {
        path: 'patients/patient/:patientId/edit',
        canActivate: [permissionGuard('patients.edit')],
        loadComponent: () =>
          import('./features/patients/patient-edit.component').then(m => m.PatientEditComponent),
      },

      // Clients — the bare list now lives in the Directory; detail/add/edit stay.
      {
        path: 'clients',
        redirectTo: 'directory',
        pathMatch: 'full',
      },
      {
        path: 'clients/add',
        canActivate: [permissionGuard('clients.edit')],
        loadComponent: () =>
          import('./features/clients/client-add.component').then(m => m.ClientAddComponent),
      },
      {
        path: 'clients/:clientId',
        canActivate: [permissionGuard('clients.view')],
        loadComponent: () =>
          import('./features/clients/client-detail.component').then(m => m.ClientDetailComponent),
      },
      {
        path: 'clients/:clientId/edit',
        canActivate: [permissionGuard('clients.edit')],
        loadComponent: () =>
          import('./features/clients/client-edit.component').then(m => m.ClientEditComponent),
      },

      // Hospitalisations
      {
        path: 'hospitalisations',
        canActivate: [permissionGuard('hospitalisations.view')],
        loadComponent: () =>
          import('./features/hospitalisations/hospitalisations-list.component').then(m => m.HospitalisationsListComponent),
      },
      {
        path: 'hospitalisations/past',
        canActivate: [permissionGuard('hospitalisations.view')],
        loadComponent: () =>
          import('./features/hospitalisations/past-hospitalisations.component').then(m => m.PastHospitalisationsComponent),
      },
      {
        path: 'hospitalisations/add',
        canActivate: [permissionGuard('hospitalisations.create')],
        loadComponent: () =>
          import('./features/hospitalisations/hospitalisation-add.component').then(m => m.HospitalisationAddComponent),
      },
      {
        path: 'hospitalisations/hospitalisation/:id',
        canActivate: [permissionGuard('hospitalisations.view')],
        loadComponent: () =>
          import('./features/hospitalisations/hospitalisation-detail.component').then(m => m.HospitalisationDetailComponent),
      },
      {
        path: 'hospitalisations/hospitalisation/:id/allRounds',
        canActivate: [permissionGuard('hospitalisations.view')],
        loadComponent: () =>
          import('./features/hospitalisations/all-rounds.component').then(m => m.AllRoundsComponent),
      },
      {
        path: 'hospitalisations/hospitalisation/:id/hospitalisationMedication',
        canActivate: [permissionGuard('hospitalisations.view')],
        loadComponent: () =>
          import('./features/hospitalisations/hospitalisation-medications.component').then(m => m.HospitalisationMedicationsComponent),
      },
      {
        path: 'hospitalisations/hospitalisation/:id/dischargeQuestionaire',
        canActivate: [permissionGuard('hospitalisations.discharge')],
        loadComponent: () =>
          import('./features/hospitalisations/discharge-questionnaire.component').then(m => m.DischargeQuestionnaireComponent),
      },
      {
        path: 'hospitalisations/hospitalisation/:id/dischargePlan',
        canActivate: [permissionGuard('hospitalisations.view')],
        loadComponent: () =>
          import('./features/hospitalisations/discharge-plan/discharge-plan.component').then(m => m.DischargePlanComponent),
      },

      // Consultations
      {
        path: 'consultations',
        canActivate: [permissionGuard('consultations.view')],
        loadComponent: () =>
          import('./features/consultations/consultations-list.component').then(m => m.ConsultationsListComponent),
      },
      {
        path: 'consultations/add',
        canActivate: [permissionGuard('consultations.create')],
        loadComponent: () =>
          import('./features/consultations/consultation-add.component').then(m => m.ConsultationAddComponent),
      },
      {
        path: 'consultations/consultation/:id',
        canActivate: [permissionGuard('consultations.view')],
        loadComponent: () =>
          import('./features/consultations/consultation-detail.component').then(m => m.ConsultationDetailComponent),
      },
      {
        path: 'consultations/consultation/:id/edit',
        canActivate: [permissionGuard('consultations.edit')],
        loadComponent: () =>
          import('./features/consultations/consultation-form.component').then(m => m.ConsultationFormComponent),
      },

      // Medications
      {
        path: 'medications',
        canActivate: [permissionGuard('medications.view')],
        loadComponent: () =>
          import('./features/medications/medications-list.component').then(m => m.MedicationsListComponent),
      },
      {
        // Add: the `new` sentinel must precede the `:medicationId` view route
        // so it isn't swallowed as an id.
        path: 'medications/medicationDetail/new',
        canActivate: [permissionGuard('medications.manage')],
        loadComponent: () =>
          import('./features/medications/medication-edit.component').then(m => m.MedicationEditComponent),
      },
      {
        path: 'medications/medicationDetail/:medicationId/edit',
        canActivate: [permissionGuard('medications.manage')],
        loadComponent: () =>
          import('./features/medications/medication-edit.component').then(m => m.MedicationEditComponent),
      },
      {
        path: 'medications/medicationDetail/:medicationId',
        canActivate: [permissionGuard('medications.view')],
        loadComponent: () =>
          import('./features/medications/medication-detail.component').then(m => m.MedicationDetailComponent),
      },
    ],
  },

  // Wildcard redirect
  { path: '**', redirectTo: '' },
];
